import React from 'react';
import { ActionIcon, Stack } from '@mantine/core';
import { useGetPuck } from '@puckeditor/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import CollapsibleListField from '../../../puck/fields/CollapsibleListField';
import InspectorRow from '../../../puck/fields/InspectorRow';
import IconPickerField from '../../../puck/fields/IconPickerField';
import { PuckColorFieldWithPalette } from '../../../puck/fields/createPuckColorField';
import { useSelectedItemProps } from '../../../puck/fields/useSelectedItemProps';
import { notifyTabPreview } from '../../../utils/tabsRegistry';
import { isTabEmpty, reconcileTabsChange } from './tabsData';
import { tabPlainLabel } from './tabsLayout';

/**
 * Champ « Onglets ».
 *
 * Un onglet n'est pas qu'une ligne de la prop `tabs` : c'est aussi une zone et
 * le Bloc qu'elle contient. Le `onChange` d'un champ Puck ne couvre que sa
 * prop, et `useSelectedItemProps` n'écrit que des props — on ignore donc les
 * deux et on pousse un `setData` unique qui écrit props ET zones ensemble
 * (cf. `tabsData.js`). L'arbre est lu à la demande via `useGetPuck` :
 * s'y abonner re-rendrait la sidebar à chaque frappe.
 *
 * Le libellé s'édite sur le canvas, pas ici (README §4) : ne reste que
 * l'icône, plus l'ajout, la suppression et le réordonnancement.
 */
export default function TabsListField({ value }) {
  const { itemProps } = useSelectedItemProps();
  const getPuck = useGetPuck();
  const blockId = itemProps.id;

  const commit = (nextTabs) => {
    const { appState, dispatch } = getPuck();
    dispatch({
      type: 'setData',
      data: reconcileTabsChange(appState.data, blockId, nextTabs),
      recordHistory: true,
    });
  };

  return (
    <CollapsibleListField
      value={value}
      onChange={commit}
      addLabel="Ajouter un onglet"
      // L'onglet ajouté n'a pas d'id : c'est ce qui dit à `reconcileTabsChange`
      // de lui créer sa zone et son Bloc.
      createItem={(count) => ({ label: `<p>Onglet ${count + 1}</p>`, icon: '', iconColor: '' })}
      itemTitle={tabPlainLabel}
      minItems={1}
      // Le contenu d'un onglet inactif est invisible au moment du clic sur la
      // corbeille : on ne le détruit pas sans le dire.
      confirmRemove={(tab, index) => {
        if (!blockId || isTabEmpty(getPuck().appState.data, blockId, tab.id)) return null;
        return `L'onglet « ${tabPlainLabel(tab, index)} » contient du contenu. `
          + 'Le supprimer supprime aussi tout ce qu\'il contient.';
      }}
      renderItemActions={(tab, index) => (
        <ActionIcon
          variant="subtle"
          color="editor"
          size="sm"
          aria-label={`Voir l'onglet ${tabPlainLabel(tab, index)} sur la page`}
          title="Voir cet onglet sur la page"
          onClick={(e) => {
            e.stopPropagation();
            if (blockId && tab.id) notifyTabPreview(blockId, tab.id);
          }}
        >
          <FontAwesomeIcon icon={icon({ name: 'eye', style: 'light' })} style={{ fontSize: 14 }} />
        </ActionIcon>
      )}
      renderItem={(tab, update) => (
        <Stack gap={12}>
          {/* Sans libellé, le picker seul ne disait pas ce qu'il réglait. */}
          <InspectorRow label="Icône">
            <IconPickerField value={tab.icon || ''} onChange={(icon) => update({ icon })} />
          </InspectorRow>
          {/* La couleur ne sert à rien sans icône : on ne la propose qu'après. */}
          {tab.icon ? (
            <PuckColorFieldWithPalette
              label="Couleur de l'icône"
              value={tab.iconColor || ''}
              onChange={(iconColor) => update({ iconColor: iconColor ?? '' })}
              allowTransparent
            />
          ) : null}
        </Stack>
      )}
    />
  );
}
